export const HEXAGON_RADIUS = 5; // 5 meter radius
export const HEXAGON_WIDTH = HEXAGON_RADIUS * 2; // 10 meter width

/**
 * Converts latitude/longitude to local meter coordinates (simple approximation)
 * @param {number} lat - Latitude in degrees
 * @param {number} lng - Longitude in degrees
 * @param {number} originLat - Origin latitude for local coordinate system
 * @param {number} originLng - Origin longitude for local coordinate system
 * @returns {object} {x, y} Local coordinates in meters
 */
export function latLngToLocalMeters(lat, lng, originLat, originLng) {
	const R = 6378137; // Earth's radius in meters
	const latRad = lat * Math.PI / 180;
	const originLatRad = originLat * Math.PI / 180;
	
	const x = (lng - originLng) * Math.PI / 180 * R * Math.cos(originLatRad);
	const y = (lat - originLat) * Math.PI / 180 * R;
	
	return { x, y };
}

/**
 * Converts local meter coordinates to latitude/longitude
 * @param {number} x - X coordinate in meters
 * @param {number} y - Y coordinate in meters
 * @param {number} originLat - Origin latitude for local coordinate system
 * @param {number} originLng - Origin longitude for local coordinate system
 * @returns {object} {lat, lng} Latitude/longitude in degrees
 */
export function localMetersToLatLng(x, y, originLat, originLng) {
	const R = 6378137; // Earth's radius in meters
	const originLatRad = originLat * Math.PI / 180;
	
	const lat = originLat + (y / R) * 180 / Math.PI;
	const lng = originLng + (x / (R * Math.cos(originLatRad))) * 180 / Math.PI;
	
	return { lat, lng };
}

/**
 * Converts hexagon coordinate to world position
 * For flat-top hexagons:
 * - Horizontal spacing: R × 3/2
 * - Vertical spacing: R × √3
 * - Every other column offset by R × √3/2
 * @param {number} q - Column coordinate
 * @param {number} r - Row coordinate
 * @param {number} radius - Hexagon radius in meters
 * @returns {object} {x, y} World coordinates in meters
 */
export function hexToWorld(q, r, radius = HEXAGON_RADIUS) {
	const x = radius * 3 / 2 * q;
	const y = radius * Math.sqrt(3) * (r + 0.5 * q);
	return { x, y };
}

/**
 * Converts world position to hexagon coordinate
 * @param {number} x - X coordinate in meters
 * @param {number} y - Y coordinate in meters
 * @param {number} radius - Hexagon radius in meters
 * @returns {object} {q, r} Hexagon coordinates
 */
export function worldToHex(x, y, radius = HEXAGON_RADIUS) {
	const q = (2 / 3 * x) / radius;
	const r = (-1 / 3 * x + Math.sqrt(3) / 3 * y) / radius;
	return hexRound(q, r);
}

/**
 * Rounds fractional hex coordinates to nearest integer hex coordinates
 * @param {number} q - Fractional q coordinate
 * @param {number} r - Fractional r coordinate
 * @returns {object} {q, r} Rounded hex coordinates
 */
export function hexRound(q, r) {
	const s = -q - r;
	
	const rq = Math.round(q);
	const rr = Math.round(r);
	const rs = Math.round(s);
	
	const q_diff = Math.abs(rq - q);
	const r_diff = Math.abs(rr - r);
	const s_diff = Math.abs(rs - s);
	
	if (q_diff > r_diff && q_diff > s_diff) {
		return { q: -rr - rs, r: rr };
	} else if (r_diff > s_diff) {
		return { q: rq, r: -rq - rs };
	} else {
		return { q: rq, r: rr };
	}
}

/**
 * Generates vertices for a flat-top hexagon
 * @param {number} centerX - Center X coordinate
 * @param {number} centerY - Center Y coordinate
 * @param {number} radius - Hexagon radius
 * @returns {Array} Array of [x, y] vertex coordinates
 */
export function generateHexagonVertices(centerX, centerY, radius = HEXAGON_RADIUS) {
	const vertices = [];
	// Flat-top hexagon: angles at 0°, 60°, 120°, 180°, 240°, 300°
	const angles = [0, 60, 120, 180, 240, 300];
	
	for (const angle of angles) {
		const angleRad = (angle * Math.PI) / 180;
		const x = centerX + radius * Math.cos(angleRad);
		const y = centerY + radius * Math.sin(angleRad);
		vertices.push([x, y]);
	}
	
	// Close the polygon
	vertices.push(vertices[0]);
	
	return vertices;
}

/**
 * Generates hexagon coordinates in a radius around a center point
 * @param {number} centerQ - Center hexagon Q coordinate
 * @param {number} centerR - Center hexagon R coordinate
 * @param {number} radius - Radius in hexagon units
 * @returns {Array} Array of {q, r} hexagon coordinates
 */
export function generateHexagonGrid(centerQ, centerR, radius) {
	const hexagons = [];
	
	for (let q = centerQ - radius; q <= centerQ + radius; q++) {
		const r1 = Math.max(centerR - radius, centerR - q - radius);
		const r2 = Math.min(centerR + radius, centerR - q + radius);
		
		for (let r = r1; r <= r2; r++) {
			hexagons.push({ q, r });
		}
	}
	
	return hexagons;
}

/**
 * Gets the 6 adjacent hexagon coordinates
 * @param {number} q - Center hexagon Q coordinate
 * @param {number} r - Center hexagon R coordinate
 * @returns {Array} Array of {q, r} adjacent hexagon coordinates
 */
export function getAdjacentHexagons(q, r) {
	const directions = [
		{ q: 1, r: 0 },   // Right
		{ q: 1, r: -1 },  // Upper right
		{ q: 0, r: -1 },  // Upper left
		{ q: -1, r: 0 },  // Left
		{ q: -1, r: 1 },  // Lower left
		{ q: 0, r: 1 }    // Lower right
	];
	
	return directions.map(dir => ({
		q: q + dir.q,
		r: r + dir.r
	}));
}

/**
 * Creates a unique ID for a hexagon
 * @param {number} q - Hexagon Q coordinate
 * @param {number} r - Hexagon R coordinate
 * @returns {string} Unique hexagon ID
 */
export function getHexagonId(q, r) {
	return `hex_${q}_${r}`;
}

/**
 * Parses a hexagon ID to get coordinates
 * @param {string} hexId - Hexagon ID
 * @returns {object} {q, r} Hexagon coordinates
 */
export function parseHexagonId(hexId) {
	if (!hexId || typeof hexId !== 'string') {
		console.error('Invalid hexId:', hexId);
		return { q: 0, r: 0 };
	}
	const parts = hexId.split('_');
	return {
		q: parseInt(parts[1]),
		r: parseInt(parts[2])
	};
}